'use client';

import { useEffect, useId, useState } from 'react';
import {
  FILTER_CATEGORIES,
  START_TIME_HINTS,
  categoryTitle,
  selectedIn,
  toggleInCategory,
  withCategory,
  type AttractionFilters as Filters,
  type FacetOption,
  type FilterCategory,
  type ListingFacets,
} from '../config';
import { ChevronUpIcon } from './Icons';
import { FilterMorePopup } from './FilterMorePopup';
import { useAttractionMoney } from './Money';

interface AttractionsFiltersProps {
  value: Filters;
  onChange: (next: Filters) => void;
  /**
   * Option lists and counts for the CURRENT result set, computed by the service
   * (BCP-2063 §2). A category with no options is hidden entirely — the sidebar
   * can never offer a checkbox that no product carries.
   */
  facets: ListingFacets;
}

/** Values shown before the overflow rule kicks in (BCP-2062 §5.2). */
const FIRST_N = 5;

/** Collapsible group shell — matches the chevron-headed sections in the design. */
function Group({
  title,
  children,
  defaultOpen = true,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();

  return (
    <section className="atr-fgroup">
      <button
        type="button"
        className="atr-fgroup__head"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((o) => !o)}
      >
        {title}
        <ChevronUpIcon />
      </button>
      {open && (
        <div className="atr-fgroup__body" id={bodyId}>
          {children}
        </div>
      )}
    </section>
  );
}

/**
 * Price Range (BCP-2062 §2, `home-page.png`).
 *
 * Two editable pills — the minimum and the maximum — over a SINGLE-thumb track
 * that drives the maximum, with the bar filled from the left edge up to the
 * thumb. That is what the design shows, and the design is the visual spec.
 *
 * I built this as a two-thumb control first, ported from the hotels vertical.
 * It works, but it is visibly not this: two handles on a track filled only
 * BETWEEN them reads as a different component. Both bounds are still settable —
 * the minimum through its pill — so BCP-2062's "users can set minimum and
 * maximum values" holds either way; the pills are the part of the control that
 * carries it.
 *
 * Bounds come from the result set WITHOUT the price filter applied, so dragging
 * inward never shrinks the track needed to drag back out.
 *
 * Committed on release, not on every pixel of drag: each commit is a URL write
 * and a fetch, and committing per pixel would queue a request per frame.
 */
function PriceRange({
  value,
  onCommit,
  bounds,
  symbol,
}: {
  value: { min: number | null; max: number | null };
  onCommit: (min: number | null, max: number | null) => void;
  bounds: { min: number; max: number };
  symbol: string;
}) {
  const floor = Math.floor(bounds.min);
  const ceiling = Math.ceil(bounds.max);
  const span = Math.max(1, ceiling - floor);
  const step = Math.max(1, Math.ceil(span / 200));

  const [localMin, setLocalMin] = useState(value.min ?? floor);
  const [localMax, setLocalMax] = useState(value.max ?? ceiling);

  // A new result set moves the bounds; an untouched handle follows them so the
  // default state never excludes a product it has no opinion about.
  useEffect(() => {
    setLocalMin(value.min ?? floor);
    setLocalMax(value.max ?? ceiling);
  }, [value.min, value.max, floor, ceiling]);

  const pct = (n: number) => ((n - floor) / span) * 100;

  const commit = (min: number, max: number) =>
    // Back at the full span → no price filter at all, rather than an explicit
    // range that happens to match it. That keeps the URL clean and stops a
    // "price" chip appearing for a filter the user effectively cleared.
    onCommit(min <= floor ? null : min, max >= ceiling ? null : max);

  return (
    <div>
      {/* `£ 5  —  £ 2217`: symbol, a space, then the amount, with an em dash
          between the two pills. */}
      <div className="atr-price-inputs">
        <label>
          <span className="atr-visually-hidden">Minimum price</span>
          <input
            type="text"
            inputMode="numeric"
            value={`${symbol} ${Math.round(localMin)}`}
            disabled 
            onChange={(e) => {
              const n = Number(e.target.value.replace(/[^0-9]/g, ''));
              setLocalMin(Number.isFinite(n) ? Math.min(Math.max(n, floor), localMax - step) : floor);
            }}
            onBlur={() => commit(localMin, localMax)}
            onKeyDown={(e) => e.key === 'Enter' && commit(localMin, localMax)}
          />
        </label>
        <span className="atr-price-inputs__dash" aria-hidden="true">
          —
        </span>
        <label>
          <span className="atr-visually-hidden">Maximum price</span>
          <input
            type="text"
            inputMode="numeric"
            value={`${symbol} ${Math.round(localMax)}`}
            onChange={(e) => {
              const n = Number(e.target.value.replace(/[^0-9]/g, ''));
              setLocalMax(
                Number.isFinite(n) ? Math.max(Math.min(n, ceiling), localMin + step) : ceiling,
              );
            }}
            disabled 
            onBlur={() => commit(localMin, localMax)}
            onKeyDown={(e) => e.key === 'Enter' && commit(localMin, localMax)}
          />
        </label>
      </div>

      <div className="atr-range-wrap">
        <div className="atr-range-track" />
        {/* Filled from the left edge to the thumb, per the design — not between
            two handles. */}
        <div className="atr-range-fill" style={{ left: 0, right: `${100 - pct(localMax)}%` }} />
        <input
          className="atr-range"
          type="range"
          min={floor}
          max={ceiling}
          step={step}
          value={localMax}
          aria-label="Maximum price"
          onChange={(e) => setLocalMax(Math.max(Number(e.target.value), localMin + step))}
          onMouseUp={() => commit(localMin, localMax)}
          onTouchEnd={() => commit(localMin, localMax)}
          onKeyUp={() => commit(localMin, localMax)}
        />
      </div>
    </div>
  );
}

/**
 * One checkbox row. `{Label} ({count})` per BCP-2062 §4 — every category except
 * Price Range shows its available result count.
 */
function CheckRow({
  option,
  checked,
  onToggle,
  hint,
}: {
  option: FacetOption;
  checked: boolean;
  onToggle: () => void;
  hint?: string;
}) {
  return (
    <label className={`atr-check atr-check--counted${hint ? ' atr-check-box' : ''}`}>
      <span>
        <input type="checkbox" checked={checked} onChange={onToggle} />
        {hint ? (
          <span>
            <span className="atr-check-box__label">{option.label}</span>
            <span className="atr-check-box__hint">{hint}</span>
          </span>
        ) : (
          // Guest Rating included: the ticket's own wording ("4.0+", "3.0+", …)
          // is what the panel displays, not a row of star icons.
          option.label
        )}
      </span>
      <span className="atr-check__count">({option.count})</span>
    </label>
  );
}

/**
 * The first-5 / More rule (BCP-2062 §5.2):
 *
 *   ≤ 5 total   all shown, no affordance
 *   6+ total    first 5 + "More (N)" → popup listing ALL values
 *
 * ONE overflow control for every category. The ticket's own table splits this
 * in two — an inline reveal at 6–10 values, the popup only at 11+ — but two
 * different affordances for the same job read as a bug rather than a rule:
 * Features offered "Show 3 more" while Attraction Type offered "More (12)"
 * directly beneath it. Product asked for the popup everywhere, so the inline
 * branch is gone.
 */
function CheckList({
  category,
  options,
  selected,
  onToggle,
  onApplyAll,
}: {
  category: FilterCategory;
  options: FacetOption[];
  selected: string[];
  onToggle: (value: string) => void;
  onApplyAll: (values: string[]) => void;
}) {
  const [popupOpen, setPopupOpen] = useState(false);

  // Measured on the RAW list, so the affordance a category shows does not
  // change as the user ticks boxes.
  const overflow = options.length - FIRST_N;

  /**
   * Only the first five options, in their original order — the Hotels listing
   * rule (`HotelsFilters` `options.slice(0, MAX_VISIBLE)`).
   *
   * This replaced "first five PLUS every selected value": selecting every value
   * in the More popup then put the whole category inline, a long list under
   * each heading. A selection beyond the first five now lives in the More popup
   * (which opens pre-checked from the committed selection) and in the chip row,
   * exactly as on Hotels.
   */
  const visible = options.slice(0, FIRST_N);

  /*
   * The More link reports the category's TOTAL option count — what the popup
   * lists — the same as the Hotels listing (`HotelsFilters` "More… (N)").
   * It used to report the options still hidden inline, which fell to
   * "More (0)" once every value was selected.
   */
  const total = options.length;

  const hintFor = (value: string) =>
    category === 'startTimes' ? START_TIME_HINTS[value] : undefined;

  return (
    <>
      {visible.map((option) => (
        <CheckRow
          key={option.value}
          option={option}
          checked={selected.includes(option.value)}
          onToggle={() => onToggle(option.value)}
          hint={hintFor(option.value)}
        />
      ))}

      {overflow > 0 && (
        <button type="button" className="mt-1 text-xs text-link-blue hover:underline text-left" onClick={() => setPopupOpen(true)}>
          More... ({total})
        </button>
      )}

      {popupOpen && (
        <FilterMorePopup
          title={categoryTitle(category)}
          // The popup lists ALL values for the category, including the five
          // already shown inline (BCP-2062 §6).
          options={options}
          selected={selected}
          onApply={onApplyAll}
          onClose={() => setPopupOpen(false)}
        />
      )}
    </>
  );
}

/**
 * Listing sidebar (`home-page.png`): Price Range, Attraction Type, Features,
 * Duration, Start Time, Guest Rating.
 *
 * Fully controlled — the parent owns the filter object, which is itself derived
 * from the URL, so the URL, the active result set, the chip row and this panel
 * can never disagree.
 *
 * Every category is DYNAMIC in the sense that matters: its presence and its
 * counts come from the current result set. That is not the same as its ORDER —
 * Duration, Start Time and Guest Rating keep the fixed display order the ticket
 * gives them and are never reordered by count (BCP-2062 §2).
 *
 * Start Time is expected to be absent in practice: Bridgify exposes start times
 * only inside a cart, so nothing in the catalogue carries one and the
 * hide-when-empty rule drops the whole category. It is wired up so that syncing
 * timeslots is the only change needed to switch it on.
 */
export function AttractionsFilters({ value, onChange, facets }: AttractionsFiltersProps) {
  const { toUserAmount, toBaseAmount, currencySymbol } = useAttractionMoney();

  // Catalogue currency the stored bounds and the API's minPrice/maxPrice are
  // denominated in. The slider works entirely in the visitor's currency: bounds
  // and current values are converted on the way in, committed values converted
  // back on the way out — so changing currency in the navbar re-labels and
  // re-scales the track, while the API keeps receiving numbers comparable with
  // the prices it stores. Same round-trip as the Shows slider's gbpToUser /
  // userToGbp pair.
  const priceBase = facets.priceRange?.currency ?? 'USD';

  return (
    <aside className="atr-filters" aria-label="Filter attractions">
      {facets.priceRange && facets.priceRange.max > facets.priceRange.min && (
        <Group title="Price Range">
          <PriceRange
            value={{
              min: value.minPrice === null ? null : toUserAmount(value.minPrice, priceBase),
              max: value.maxPrice === null ? null : toUserAmount(value.maxPrice, priceBase),
            }}
            bounds={{
              min: toUserAmount(facets.priceRange.min, priceBase),
              max: toUserAmount(facets.priceRange.max, priceBase),
            }}
            symbol={currencySymbol}
            onCommit={(min, max) =>
              onChange({
                ...value,
                // Widen rather than narrow when rounding, so a bound sitting on
                // a product's exact price cannot round it out of its own range.
                minPrice: min === null ? null : Math.floor(toBaseAmount(min, priceBase)),
                maxPrice: max === null ? null : Math.ceil(toBaseAmount(max, priceBase)),
              })
            }
          />
        </Group>
      )}

      {FILTER_CATEGORIES.map(({ key }) => {
        const options = facets[key];
        // "Show only those filter categories which have results ≥ 1"
        // (BCP-2063 §2) — hide the whole block, not an empty container and not
        // a disabled state.
        if (!options.length) return null;

        return (
          <Group key={key} title={categoryTitle(key)}>
            <CheckList
              category={key}
              options={options}
              selected={selectedIn(value, key)}
              onToggle={(v) => onChange(toggleInCategory(value, key, v))}
              onApplyAll={(values) => onChange(withCategory(value, key, values))}
            />
          </Group>
        );
      })}
    </aside>
  );
}
